import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stages = ['Cutting', 'Forging', 'Machining', 'HT', 'FG'];
const VALID_TRANSITIONS = {
  Cutting: ['Forging'],
  Forging: ['Machining', 'HT', 'FG'],
  Machining: ['HT', 'FG'],
  HT: ['Machining', 'FG'],
};

export default function StageIssue() {
  const [issues, setIssues] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [usableStock, setUsableStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [lineQtys, setLineQtys] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    part_id: '',
    stage_from: '',
    stage_to: '',
    vendor_id: '',
    qty: ''
  });

  const fetchIssues = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/stage-issue`);
      setIssues(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load stage issues');
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`);
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/vendors`);
      setVendors(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchIssues();
    fetchItems();
    fetchVendors();
  }, [fetchIssues, fetchItems, fetchVendors]);

  const fetchUsableStock = useCallback(async (partId) => {
    if (!partId) { setUsableStock([]); return; }
    setLoadingStock(true);
    try {
      const response = await axios.get(`${API}/transactions/usable-stock/${encodeURIComponent(partId)}`);
      setUsableStock(response.data.stages || []);
    } catch (err) { console.error(err);
      toast.error('Failed to load usable stock');
      setUsableStock([]);
    } finally { setLoadingStock(false); }
  }, []);

  // Refresh usable stock when part changes (create mode only)
  useEffect(() => {
    if (!editingId && formData.part_id) fetchUsableStock(formData.part_id);
  }, [formData.part_id, editingId, fetchUsableStock]);

  const resetForm = () => {
    setFormData({ date: new Date().toISOString().split('T')[0], part_id: '', stage_from: '', stage_to: '', vendor_id: '', qty: '' });
    setEditingId(null);
    setShowForm(false);
    setUsableStock([]);
    setLineQtys({});
  };

  const handleEdit = (entry) => {
    setFormData({
      date: new Date(entry.date).toISOString().split('T')[0],
      part_id: entry.part_id,
      stage_from: entry.stage_from,
      stage_to: entry.stage_to,
      vendor_id: entry.vendor_id || '',
      qty: entry.qty.toString()
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const stageEligible = (stage) => formData.stage_to && (VALID_TRANSITIONS[stage] || []).includes(formData.stage_to);
  const eligibleLines = Object.entries(lineQtys)
    .filter(([stage]) => stageEligible(stage))
    .map(([stage, qty]) => ({ stage_from: stage, qty: parseInt(qty) || 0 }))
    .filter(l => l.qty > 0);
  const totalIssueQty = eligibleLines.reduce((s, l) => s + l.qty, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    if (editingId) {
      try {
        await axios.put(`${API}/transactions/stage-issue/${editingId}`, {
          date: new Date(formData.date).toISOString(),
          part_id: formData.part_id,
          stage_from: formData.stage_from,
          stage_to: formData.stage_to,
          vendor_id: formData.vendor_id || null,
          qty: parseInt(formData.qty),
          created_by: currentUser.username
        });
        toast.success('Stage issue updated successfully');
        resetForm();
        fetchIssues();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save stage issue');
      }
      return;
    }

    // Create mode: multi-stage issue
    const lines = eligibleLines;
    if (!formData.part_id || !formData.stage_to) {
      toast.error('Select a part and destination stage');
      return;
    }
    if (lines.length === 0) {
      toast.error('Enter a quantity against at least one source stage');
      return;
    }
    // Client-side usable validation
    for (const l of lines) {
      const row = usableStock.find(r => r.stage === l.stage_from);
      if (row && l.qty > row.usable) {
        toast.error(`Insufficient usable stock at ${l.stage_from}. Usable: ${row.usable}, requested: ${l.qty}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/transactions/stage-issue-multi`, {
        date: new Date(formData.date).toISOString(),
        part_id: formData.part_id,
        stage_to: formData.stage_to,
        vendor_id: formData.vendor_id || null,
        lines,
        created_by: currentUser.username
      });
      toast.success(response.data.message);
      resetForm();
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stage issue');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stage issue?')) return;
    try {
      await axios.delete(`${API}/transactions/stage-issue/${id}`);
      toast.success('Stage issue deleted successfully');
      fetchIssues();
    } catch (error) {
      toast.error('Failed to delete stage issue');
    }
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const monthGroups = groupByMonth(issues);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="stage-issue-title">STAGE ISSUE</h1>
          <p className="text-sm text-gray-700 mt-2">Issue parts to next stage or vendor — from one or multiple source stages</p>
        </div>
        <Button
          data-testid="toggle-issue-form-button"
          onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          {showForm ? <><X className="w-4 h-4 mr-2" />CANCEL</> : <><Plus className="w-4 h-4 mr-2" />NEW ISSUE</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>{editingId ? 'EDIT STAGE ISSUE' : 'NEW STAGE ISSUE'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
                <Input
                  data-testid="issue-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  className="mt-1.5 rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part ID</Label>
                <SearchableSelect
                  testId="issue-part-id-select"
                  className="mt-1.5"
                  options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                  value={formData.part_id}
                  onValueChange={(value) => { setFormData({...formData, part_id: value}); setLineQtys({}); }}
                  placeholder="Search part..."
                  searchPlaceholder="Type to search parts..."
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage To</Label>
                <Select
                  value={formData.stage_to}
                  onValueChange={(value) => { setFormData({...formData, stage_to: value}); setLineQtys({}); }}
                  required
                >
                  <SelectTrigger data-testid="issue-stage-to-select" className="mt-1.5 rounded-none">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor (Optional)</Label>
                <Select
                  value={formData.vendor_id || undefined}
                  onValueChange={(value) => setFormData({...formData, vendor_id: value})}
                >
                  <SelectTrigger data-testid="issue-vendor-select" className="mt-1.5 rounded-none">
                    <SelectValue placeholder="None (Internal)" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>{vendor.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editingId ? (
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage From</Label>
                  <Select
                    value={formData.stage_from}
                    onValueChange={(value) => setFormData({...formData, stage_from: value})}
                    required
                  >
                    <SelectTrigger data-testid="issue-stage-from-select" className="mt-1.5 rounded-none">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map(stage => (
                        <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
                  <Input
                    data-testid="issue-qty-input"
                    type="number"
                    value={formData.qty}
                    onChange={(e) => setFormData({...formData, qty: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
              </div>
            ) : formData.part_id && (
              <div className="border border-border rounded-sm" data-testid="usable-stock-table">
                <div className="px-4 py-2 bg-gray-50 border-b border-border flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Usable Stock by Stage — enter qty to issue from each stage</span>
                  {loadingStock && <span className="text-xs text-gray-500">Loading...</span>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/50 text-[11px] uppercase tracking-wider text-gray-600 border-b border-border">
                      <th className="text-left px-4 py-2">Stage</th>
                      <th className="text-right px-4 py-2">On Hand</th>
                      <th className="text-right px-4 py-2">Pending Issue</th>
                      <th className="text-right px-4 py-2">Usable</th>
                      <th className="text-right px-4 py-2 w-40">Issue Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usableStock.map(row => {
                      const eligible = stageEligible(row.stage) && row.usable > 0;
                      const reason = !formData.stage_to ? 'Select Stage To first'
                        : !(VALID_TRANSITIONS[row.stage] || []).includes(formData.stage_to) ? `Cannot issue ${row.stage} → ${formData.stage_to}`
                        : row.usable <= 0 ? 'No usable stock' : '';
                      return (
                        <tr key={row.stage} className={`border-b border-border last:border-b-0 ${eligible ? '' : 'opacity-50'}`} data-testid={`usable-stock-row-${row.stage}`}>
                          <td className="px-4 py-2 font-semibold">{row.stage}</td>
                          <td className="px-4 py-2 text-right font-mono">{row.on_hand}</td>
                          <td className="px-4 py-2 text-right font-mono text-amber-700">{row.pending_issue}</td>
                          <td className={`px-4 py-2 text-right font-mono font-bold ${row.usable > 0 ? 'text-green-700' : 'text-gray-400'}`} data-testid={`usable-qty-${row.stage}`}>{row.usable}</td>
                          <td className="px-4 py-2 text-right">
                            {eligible ? (
                              <Input
                                data-testid={`issue-qty-input-${row.stage}`}
                                type="number"
                                min="0"
                                placeholder="0"
                                value={lineQtys[row.stage] || ''}
                                onChange={(e) => setLineQtys(prev => ({ ...prev, [row.stage]: e.target.value }))}
                                className="rounded-none h-8 text-right font-mono"
                              />
                            ) : (
                              <span className="text-[11px] text-gray-400">{reason}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-border">
                      <td colSpan={4} className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-700">Total to issue {formData.stage_to ? `→ ${formData.stage_to}` : ''}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-base" data-testid="total-issue-qty">{totalIssueQty}</td>
                    </tr>
                  </tfoot>
                </table>
                {eligibleLines.length > 1 && (
                  <div className="px-4 py-2 bg-blue-50 border-t border-blue-200 text-xs text-blue-800">
                    {eligibleLines.length} separate stage issues and challans will be created — one per source stage.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                data-testid="submit-issue-button"
                type="submit"
                disabled={submitting || (!editingId && totalIssueQty === 0)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                {editingId ? 'UPDATE ISSUE' : submitting ? 'CREATING...' : `CREATE ISSUE${eligibleLines.length > 1 ? 'S' : ''}`}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT ISSUES</h2>
        </div>
        {issues.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No stage issues found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage From</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage To</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              {monthGroups.map(group => (
                <tbody key={`group-${group.key}`}>
                  <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                    <td colSpan={7} className="p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                        {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {group.label} ({group.entries.length} entries)
                      </div>
                    </td>
                  </tr>
                  {!collapsedMonths[group.key] && group.entries.map((issue, idx) => (
                    <tr key={issue.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`issue-row-${issue.id || idx}`}>
                      <td className="p-4 align-middle font-mono text-xs">{new Date(issue.date).toLocaleDateString()}</td>
                      <td className="p-4 align-middle font-mono text-xs">{issue.part_id}</td>
                      <td className="p-4 align-middle">
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-100 text-slate-700">
                          {issue.stage_from}
                        </span>
                      </td>
                      <td className="p-4 align-middle">
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-emerald-50 text-emerald-700">
                          {issue.stage_to}
                        </span>
                      </td>
                      <td className="p-4 align-middle font-mono text-xs">{issue.vendor_id || 'Internal'}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs">{issue.qty}</td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center justify-center gap-1">
                          {canModify(issue) && (<>
                            <button
                              data-testid={`edit-issue-${issue.id || idx}`}
                              onClick={() => handleEdit(issue)}
                              className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`delete-issue-${issue.id || idx}`}
                              onClick={() => handleDelete(issue.id)}
                              className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
